import { useState, useEffect, useRef } from 'react';
import { AnimatePresence, motion, LayoutGroup, useReducedMotion } from 'framer-motion';
import type { EleicaoSnapshot, EleicaoCandidato } from '@/shared/types';
import { tituloCargo } from '../../domain/usecases/cargos';
import { corPartido } from '../../domain/usecases/corPartido';
import {
  formatarPercentual,
  formatarVotos,
  formatarHorario,
} from '../../domain/usecases/formatarEleicao';

export interface TvResultadoEleicaoProps {
  snapshot: EleicaoSnapshot | null;
  reduzirMovimento?: boolean;
  /** Sobe a faixa (mesmo deslocamento do logo) quando o RAIO-X ocupa o rodapé. */
  elevado?: boolean;
}

interface CandidatoFotoProps {
  url: string;
  nome: string;
  sqcand: string;
  corFundo: string;
  corTexto: string;
  rank: number;
  /** Borda diagonal do partido na lateral interna: à direita da foto (1º) ou à esquerda (2º, espelhado). */
  espelhado: boolean;
}

// Painel da foto: ocupa a lateral inteira do cartão (sem tingir o rosto); o fundo é a cor do partido e a
// foto é cortada na diagonal, deixando uma cunha colorida entre a foto e o texto.
const PAINEL_FOTO = 'relative w-[clamp(100px,11vw,220px)] shrink-0 self-stretch overflow-hidden bg-slate-100';

function CandidatoFoto({ url, nome, sqcand, corFundo, corTexto, rank, espelhado }: CandidatoFotoProps) {
  const [status, setStatus] = useState<'carregando' | 'carregado' | 'erro'>('carregando');
  const imgRef = useRef<HTMLImageElement>(null);

  // Não reseta o status aqui: foto em cache dispara onLoad antes deste efeito, e voltar para
  // 'carregando' faria o timeout trocar uma foto já exibida pelo placeholder. Troca de URL
  // remonta o componente (key={url} no chamador), então o estado inicial já vale para cada URL.
  useEffect(() => {
    if (!url) {
      setStatus('erro');
      return;
    }

    // Foto em cache (pré-carregada pelo hook) já está completa no mount e pode não disparar onLoad.
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) {
      setStatus('carregado');
      return;
    }

    const timer = setTimeout(() => {
      setStatus((atual) => (atual === 'carregando' ? 'erro' : atual));
    }, 6_000);

    return () => {
      clearTimeout(timer);
    };
  }, [url]);

  // Fundo na cor do partido (a cunha aparece onde o recorte diagonal da foto abre espaço) + selo da posição.
  const fundoPartido = (
    <div
      data-testid={`faixa-partido-${sqcand}`}
      className="absolute inset-0"
      style={{ backgroundColor: corFundo }}
    />
  );
  const recorte = espelhado
    ? 'polygon(14% 0, 100% 0, 100% 100%, 0 100%)'
    : 'polygon(0 0, 100% 0, 86% 100%, 0 100%)';
  const selo = (
    <span
      className={`absolute top-[0.5vw] rounded-lg px-[0.6vw] py-[0.1vw] text-[clamp(12px,1.2vw,24px)] font-black leading-tight shadow-md ring-2 ring-white ${espelhado ? 'right-[0.5vw]' : 'left-[0.5vw]'}`}
      style={{ backgroundColor: corFundo, color: corTexto }}
    >
      {rank}º
    </span>
  );

  if (status === 'erro' || !url) {
    return (
      <div
        data-testid={`foto-placeholder-${sqcand}`}
        className={`${PAINEL_FOTO} flex items-end justify-center`}
        aria-hidden="true"
      >
        {fundoPartido}
        <svg viewBox="0 0 24 24" className="relative h-[80%] w-[80%] fill-white/50">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
        {selo}
      </div>
    );
  }

  return (
    <div className={PAINEL_FOTO}>
      {fundoPartido}
      <img
        ref={imgRef}
        src={url}
        alt={nome}
        onLoad={() => setStatus('carregado')}
        onError={() => setStatus('erro')}
        className="relative h-full w-full object-cover object-top"
        style={{ clipPath: recorte }}
      />
      {selo}
    </div>
  );
}

function useAnimatedValor(
  valorAlvo: number,
  reduzirMovimento: boolean,
  duracaoMs = 600,
): number {
  const [valor, setValor] = useState(valorAlvo);
  const valorAnteriorRef = useRef(valorAlvo);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    if (reduzirMovimento) {
      setValor(valorAlvo);
      valorAnteriorRef.current = valorAlvo;
      return;
    }

    const valorInicial = valorAnteriorRef.current;
    if (valorInicial === valorAlvo) {
      setValor(valorAlvo);
      return;
    }

    const inicioMs = performance.now();

    const tick = (agoraMs: number) => {
      const progresso = Math.min(1, (agoraMs - inicioMs) / duracaoMs);
      const ease = 1 - Math.pow(1 - progresso, 2);
      const atual = valorInicial + (valorAlvo - valorInicial) * ease;
      setValor(atual);

      if (progresso < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setValor(valorAlvo);
        valorAnteriorRef.current = valorAlvo;
      }
    };

    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [valorAlvo, duracaoMs, reduzirMovimento]);

  return valor;
}

/** Destaque da situação oficial: Eleito em verde, 2º turno em âmbar, outro texto do TSE em azul-marinho. */
function classeSituacao(situacao: string): string {
  const s = situacao.trim().toLowerCase();
  if (s.startsWith('eleito')) return 'bg-green-600 text-white';
  if (s.includes('2º turno') || s.includes('2o turno') || s.includes('segundo turno')) return 'bg-amber-400 text-[#1a1a1a]';
  return 'bg-[#0b1b3f] text-white';
}

interface CandidatoCardProps {
  cand: EleicaoCandidato;
  reduceMotion: boolean;
  /** O 2º colocado é espelhado: foto à direita e texto alinhado à direita. */
  espelhado: boolean;
}

function CandidatoCard({ cand, reduceMotion, espelhado }: CandidatoCardProps) {
  const percentualAnimado = useAnimatedValor(cand.percentual, reduceMotion);
  const votosAnimados = useAnimatedValor(cand.votos, reduceMotion);
  const cor = corPartido(cand.partido);

  return (
    <motion.div
      key={cand.sqcand}
      layout={!reduceMotion}
      data-reduced-motion={reduceMotion ? 'true' : 'false'}
      transition={
        reduceMotion
          ? { duration: 0 }
          : { type: 'spring', stiffness: 350, damping: 30 }
      }
      data-testid={`candidato-card-${cand.sqcand}`}
      aria-label={`${cand.rank}º colocado`}
      className={`flex items-stretch overflow-hidden rounded-2xl bg-white shadow-[0_8px_28px_rgba(0,0,0,0.35)] ${espelhado ? 'flex-row-reverse' : ''}`}
    >
      <CandidatoFoto
        key={cand.foto_url}
        url={cand.foto_url}
        nome={cand.nome}
        sqcand={cand.sqcand}
        corFundo={cor.fundo}
        corTexto={cor.texto}
        rank={cand.rank}
        espelhado={espelhado}
      />

      <div className={`flex min-w-0 flex-1 flex-col justify-between p-[0.9vw] ${espelhado ? 'items-end text-right' : 'items-start text-left'}`}>
        <div>
          <p className={`flex min-h-[2.1em] items-end text-[clamp(14px,1.6vw,30px)] font-black uppercase leading-[1.05] tracking-tight text-[#0b1b3f] ${espelhado ? 'justify-end' : ''}`}>
            {cand.nome}
          </p>
          <div className={`mt-[0.15vw] flex items-center gap-[0.5vw] ${espelhado ? 'justify-end' : ''}`}>
            <p className="text-[clamp(11px,1.05vw,20px)] font-semibold uppercase text-slate-500">
              {cand.partido}
            </p>
            {cand.situacao && (
              <span
                data-testid={`situacao-${cand.sqcand}`}
                className={`whitespace-nowrap rounded-md px-[0.6vw] py-[0.1vw] text-[clamp(11px,0.95vw,18px)] font-extrabold ${classeSituacao(cand.situacao)}`}
              >
                {cand.situacao}
              </span>
            )}
          </div>
        </div>

        <div>
          <p
            data-testid={`percentual-${cand.sqcand}`}
            className="text-[clamp(28px,3.9vw,76px)] font-black leading-none tracking-tight tabular-nums"
            style={{ color: cor.sobreBranco }}
          >
            {formatarPercentual(percentualAnimado)}
          </p>
          <p className="mt-[0.2vw] text-[clamp(11px,1.15vw,22px)] font-medium tabular-nums text-slate-500">
            <span data-testid={`votos-${cand.sqcand}`}>{formatarVotos(Math.round(votosAnimados))}</span>
            {' '}votos
          </p>
        </div>

        <div className="mt-[0.5vw] h-[clamp(8px,0.8vw,16px)] w-full overflow-hidden rounded-full bg-slate-200">
          <motion.div
            className={`h-full rounded-full ${espelhado ? 'ml-auto' : ''}`}
            style={{ backgroundColor: cor.fundo }}
            initial={false}
            animate={{ width: `${Math.min(100, Math.max(0, cand.percentual))}%` }}
            transition={reduceMotion ? { duration: 0 } : { duration: 0.8, ease: 'easeOut' }}
          />
        </div>
      </div>
    </motion.div>
  );
}

// O centralizamento é da classe Tailwind `-translate-x-1/2` (propriedade `translate` no v4):
// sem transform: translateX inline para não conflitar com a classe Tailwind.
const CONTAINER_STYLE = {
  filter: 'drop-shadow(0 14px 40px rgba(0,0,0,0.55))',
};

// Escala do painel completo (-20%): reduz tudo junto, mantendo as proporções. Ancorada na base para
// não mexer na posição inferior. Usa a propriedade `scale` (Tailwind v4), que compõe com `translate`.
const ESCALA = 'scale-[0.8] origin-bottom';

// Mesmo deslocamento do logo em PlayerContent quando o RAIO-X aparece.
const ELEVACAO = '-translate-y-[clamp(96px,9vw,150px)]';

const BARRA_BRANCA = 'rounded-2xl bg-white shadow-[0_8px_28px_rgba(0,0,0,0.35)]';

/**
 * Faixa de resultados da eleição oficial (TSE) para TV — centro inferior da coluna de vídeo.
 *
 * - Minimalista: dois cartões brancos espelhados (foto na lateral com faixa na cor do partido, nome, partido,
 *   percentual na cor do partido, votos e barra) e uma barra branca de apuração embaixo.
 * - Antes da apuração (aguardando) mostra os 2 candidatos com 0% marcados como "ilustrativo";
 *   ao começar vira o real.
 * - Carrossel de cargos: o cargo da vez vem em `snapshot.cargo` (título no rodapé) e a troca é um fade.
 * - Foto com timeout de 6 s para placeholder; animação numérica entre atualizações.
 * - Respeita reduced motion desativando animações de layout e tween.
 */
export default function TvResultadoEleicao({
  snapshot,
  reduzirMovimento,
  elevado = false,
}: TvResultadoEleicaoProps) {
  const prefersReduced = Boolean(useReducedMotion());
  const reduceMotion = reduzirMovimento ?? prefersReduced;

  if (!snapshot) return null;

  const isAguardando = snapshot.status === 'aguardando';
  const isFinalizado = snapshot.status === 'finalizado';
  const candidatos = snapshot.candidatos.slice(0, 2);
  const progresso = Math.min(100, Math.max(0, isFinalizado ? 100 : snapshot.progresso ?? 0));
  // Diferença em pontos percentuais entre o 1º e o 2º; só com apuração rodando e vantagem real.
  const lideranca = !isAguardando && candidatos.length === 2 && candidatos[0].percentual > candidatos[1].percentual
    ? {
        pontos: candidatos[0].percentual - candidatos[1].percentual,
        votos: Math.max(0, candidatos[0].votos - candidatos[1].votos),
      }
    : null;

  // Sem candidatos (cache vazio): só a faixa fina. Com os 2 ilustrativos, o painel completo com 0%.
  if (isAguardando && candidatos.length === 0) {
    return (
      <div
        className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none select-none w-[clamp(480px,46vw,900px)] transition-transform duration-300 ${elevado ? ELEVACAO : ''}`}
        style={CONTAINER_STYLE}
        aria-live="polite"
      >
        <div className={`${BARRA_BRANCA} flex items-center justify-between px-[1.6vw] py-[0.8vw]`}>
          <p className="text-[clamp(14px,1.5vw,28px)] font-black uppercase tracking-wide text-[#0b1b3f]">
            {tituloCargo(snapshot.cargo)}
          </p>
          <p className="text-[clamp(11px,1vw,19px)] font-semibold text-slate-500">
            Aguardando início da apuração · Fonte: TSE
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`absolute bottom-6 left-1/2 -translate-x-1/2 z-10 pointer-events-none select-none w-[clamp(680px,60vw,1180px)] ${ESCALA} transition-transform duration-300 ${elevado ? ELEVACAO : ''}`}
      style={CONTAINER_STYLE}
      aria-live="polite"
    >
      <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={snapshot.cargo ?? 'presidente'}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: 'easeOut' }}
      >
      {/* Candidatos (Top 2) */}
      <LayoutGroup>
        <div className="relative grid grid-cols-2 gap-[0.8vw]">
          {candidatos.map((cand, i) => (
            <CandidatoCard key={cand.sqcand} cand={cand} reduceMotion={reduceMotion} espelhado={i === 1} />
          ))}
          {lideranca != null && (
            <div
              data-testid="diferenca-lideres"
              className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0b1b3f] px-[0.9vw] py-[0.35vw] text-center text-white shadow-lg ring-2 ring-white"
            >
              <p className="text-[clamp(11px,1.1vw,21px)] font-black tabular-nums leading-none">
                +{lideranca.pontos.toFixed(2).replace('.', ',')} p.p.
              </p>
              <p className="mt-[0.2vw] text-[clamp(8px,0.75vw,14px)] leading-none tabular-nums text-blue-200">
                {formatarVotos(lideranca.votos)} votos
              </p>
            </div>
          )}
        </div>
      </LayoutGroup>

      {/* Apuração + horário + fonte */}
      <div className={`${BARRA_BRANCA} mt-[0.8vw] flex items-center gap-[1.2vw] whitespace-nowrap px-[1.4vw] py-[0.6vw] text-[#0b1b3f]`}>
        <div className="leading-tight">
          <p className="text-[clamp(12px,1.35vw,26px)] font-black uppercase">
            {isFinalizado ? 'Apuração encerrada' : isAguardando ? 'Aguardando início da apuração' : 'Apuração'}
          </p>
          <p className="text-[clamp(9px,0.8vw,15px)] font-medium text-slate-500">
            {tituloCargo(snapshot.cargo)}{snapshot.turno ? `, ${snapshot.turno}º turno` : ''}
          </p>
        </div>

        {isAguardando ? (
          <span className="rounded-full bg-amber-400 px-[0.9vw] py-[0.2vw] text-[clamp(10px,0.95vw,18px)] font-extrabold text-[#1a1a1a]">
            Ilustrativo
          </span>
        ) : (
          <>
            <span className="h-[2vw] w-px bg-slate-300" />
            <p
              data-testid="progresso-apuracao"
              className="text-[clamp(16px,1.9vw,36px)] font-black tabular-nums leading-none"
            >
              {formatarPercentual(isFinalizado ? 100 : snapshot.progresso)}
            </p>
            <div className="h-[clamp(8px,0.8vw,16px)] min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200">
              <motion.div
                className="h-full rounded-full bg-green-500"
                initial={false}
                animate={{ width: `${progresso}%` }}
                transition={reduceMotion ? { duration: 0 } : { duration: 0.8, ease: 'easeOut' }}
              />
            </div>
          </>
        )}

        {!isAguardando && snapshot.atualizado_em && (
          <div className="flex items-baseline gap-[0.4vw] border-l border-slate-300 pl-[1vw] text-[clamp(10px,0.95vw,18px)] font-medium text-slate-500">
            <span>Atualizado às</span>
            <span className="text-[clamp(14px,1.4vw,26px)] font-black tabular-nums text-[#0b1b3f]">
              {formatarHorario(snapshot.atualizado_em)}
            </span>
          </div>
        )}
        <p className="ml-auto border-l border-slate-300 pl-[1vw] text-[clamp(10px,0.95vw,18px)] font-bold text-slate-500">
          Fonte: TSE
        </p>
      </div>
      </motion.div>
      </AnimatePresence>
    </div>
  );
}
